import { t } from '@lingui/macro';
import { GraphActuationsOptionsFn, GraphActuationsType, SiteActuationsModel } from '@shared/models';
import { useMemo } from 'react';
import { MaintenanceGraph } from '../MaintenanceGraphs/components';
import { MaintenanceGraphModel, MaintenanceUniqGraphModel } from '../MaintenanceGraphs/MaintenanceGraphs.models';

interface ActuationsProps {
  actuationsData: SiteActuationsModel[];
  actuationsLoading: boolean;
  width: number;
  solenoidActuationThreshold?: number;
  /**
   * `dataTestId` - name of an Actuations (preferably `<name>-actuations`)
   * @example
   * ```tsx
   * <Actuations {...props} dataTestId="test-actuations" />
   */
  dataTestId: string;
}

export const ACTUATIONS_GRAPH_SCALE_NAME = 'Actuations';

export const Actuations: React.FC<ActuationsProps> = ({
  actuationsData,
  actuationsLoading,
  width,
  solenoidActuationThreshold,
  dataTestId,
}) => {
  const graphData = useMemo(() => {
    const positive: MaintenanceUniqGraphModel[] = [];
    const negative: MaintenanceUniqGraphModel[] = [];
    const total: MaintenanceUniqGraphModel[] = [];
    const threshold: MaintenanceUniqGraphModel[] = [];

    const data = actuationsData?.[0];

    data?.actuationData?.forEach((ad) => {
      positive.push({ date: ad.date, value: ad?.positiveActuations ?? null });
      negative.push({ date: ad.date, value: ad?.negativeActuations ?? null });
      total.push({
        date: ad.date,
        value:
          Number.isFinite(ad?.negativeActuations) && Number.isFinite(ad?.positiveActuations)
            ? ad?.negativeActuations + ad?.positiveActuations
            : null,
      });
      threshold.push({
        date: ad.date,
        value: solenoidActuationThreshold ?? null,
      });
    });

    const GraphActuationsOptions = GraphActuationsOptionsFn();

    return [
      {
        siteId: data?.siteId,
        data: positive ?? [],
        customColor: GraphActuationsOptions[GraphActuationsType.Positive].color,
      },
      {
        siteId: data?.siteId,
        data: negative ?? [],
        customColor: GraphActuationsOptions[GraphActuationsType.Negative].color,
      },
      {
        siteId: data?.siteId,
        data: total ?? [],
        customColor: GraphActuationsOptions[GraphActuationsType.Total].color,
      },
      ...(Number.isFinite(solenoidActuationThreshold)
        ? [
            {
              siteId: data?.siteId,
              data: threshold ?? [],
              customColor: GraphActuationsOptions[GraphActuationsType.Recommended].color,
            },
          ]
        : []),
    ] as MaintenanceGraphModel<MaintenanceUniqGraphModel>[];
  }, [actuationsData, solenoidActuationThreshold]);

  return (
    <MaintenanceGraph
      title={t({ message: 'Actuations for PRV' })}
      labels={[
        t`Positive Actuations`,
        t`Negative Actuations`,
        t`Total Actuations`,
        Number.isFinite(solenoidActuationThreshold)
          ? `${t`Recommended Maximum Actuator Limit`} ${solenoidActuationThreshold ?? ''}`
          : null,
      ].filter(Boolean)}
      loading={actuationsLoading}
      width={width}
      gData={graphData}
      noDataMsg={t`There is no data for Actuations Graph`}
      actuationsGraph
      dataTestId={`${dataTestId}-actuations-maintenance-graph`}
    />
  );
};
